import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import {
  EPISODE_STATUS_LABELS,
  PatientIdSchema,
  type PatientId,
} from '@datazo/contracts';
import { Button } from '@/components/ui/button';
import { ageFromBirthdate, formatShortDateEs } from '@/shared/lib/format';
import { usePatientById } from '@/features/patients/api';
import { useProceduresList } from '@/features/procedures/api';
import { useUsersList } from '@/features/users/api';

/**
 * Vista imprimible de la historia clínica de un paciente.
 *
 * Diseño:
 *   - Encabezado institucional (Endomedical IPS) — visible en pantalla y papel.
 *   - Datos demográficos completos.
 *   - Tabla cronológica de episodios con estado y documentos firmados.
 *   - Pie de página con fecha de generación y disclaimer.
 *
 * El layout NO usa AppShell — se renderiza en pantalla completa para que la
 * impresión salga limpia (ver `.no-print` en `globals.css`). El botón
 * "Imprimir" llama `window.print()` que respeta la hoja de estilos `@media print`.
 *
 * NOTA legal: este es un documento de referencia clínica. El expediente
 * legalmente vinculante son los PDFs firmados (consent/anestesia/gasto) que
 * estarán disponibles cuando se sume el módulo `pdf-storage` con firma X.509.
 */

function isPatientId(value: string | undefined): value is PatientId {
  if (value === undefined) return false;
  return PatientIdSchema.safeParse(value).success;
}

export function PatientPrintPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const validatedId = isPatientId(idParam) ? idParam : undefined;

  const patientQuery = usePatientById(validatedId);
  const proceduresQuery = useProceduresList({
    page: 1,
    pageSize: 100,
    ...(validatedId !== undefined ? { pacienteId: validatedId } : {}),
  });
  const usersQuery = useUsersList({ page: 1, pageSize: 100 });

  // Auto-foco al botón de imprimir para que un Enter abra el diálogo.
  useEffect(() => {
    document.title = patientQuery.data
      ? `Historia clínica · ${patientQuery.data.nombres} ${patientQuery.data.apellidos}`
      : 'Historia clínica';
  }, [patientQuery.data]);

  const userById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersQuery.data?.items ?? []) {
      map.set(u.id, u.nombre);
    }
    return map;
  }, [usersQuery.data]);

  const procedures = useMemo(() => {
    const items = proceduresQuery.data?.items ?? [];
    // Cronológico ascendente para que se lea como una historia.
    return [...items].sort((a, b) => {
      const da = `${a.fecha}T${a.hora}`;
      const db = `${b.fecha}T${b.hora}`;
      return da < db ? -1 : da > db ? 1 : 0;
    });
  }, [proceduresQuery.data]);

  if (validatedId === undefined) {
    return <ErrorState message="Identificador de paciente inválido." />;
  }
  if (patientQuery.isLoading) {
    return <LoadingState />;
  }
  if (patientQuery.isError || patientQuery.data === undefined) {
    return (
      <ErrorState
        message={
          patientQuery.isError
            ? (patientQuery.error?.message ?? 'Error al cargar el paciente')
            : 'Paciente no encontrado.'
        }
      />
    );
  }

  const patient = patientQuery.data;
  const edad = ageFromBirthdate(patient.fechaNacimiento);
  const now = new Date();
  const generatedAt = now.toLocaleString('es-CO', {
    dateStyle: 'long',
    timeStyle: 'short',
  });

  return (
    <div className="min-h-screen bg-white text-black print:bg-white">
      {/* Toolbar superior — visible solo en pantalla */}
      <div className="no-print bg-surface-alt border-b border-border px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/pacientes/${patient.id}`}>
            <ArrowLeft className="w-4 h-4" /> Volver al expediente
          </Link>
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="w-4 h-4" /> Imprimir / Guardar como PDF
        </Button>
      </div>

      <div className="max-w-[820px] mx-auto px-8 py-8 print:px-0 print:py-0">
        {/* Encabezado institucional */}
        <header className="border-b-2 border-slate-900 pb-4 mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <img
                src="/logo-endomedical.png"
                alt="Endomedical"
                className="h-12 w-auto object-contain"
              />
              <div className="text-xs text-slate-600">
                Servicios de endoscopia digestiva
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-700">
            <div className="font-semibold uppercase tracking-wider text-slate-500 mb-0.5">
              Documento de referencia
            </div>
            <div>Historia clínica resumida</div>
            <div className="mt-1 text-slate-500">Generado: {generatedAt}</div>
          </div>
        </header>

        {/* Datos del paciente */}
        <section className="mb-8">
          <h2 className="text-base font-bold uppercase tracking-wider text-slate-700 border-l-4 border-slate-900 pl-2 mb-3">
            Datos del paciente
          </h2>
          <table className="w-full text-sm border-collapse">
            <tbody>
              <KeyValueRow
                left={['Nombre completo', `${patient.nombres} ${patient.apellidos}`]}
                right={['Sexo', patient.sexo === 'F' ? 'Femenino' : patient.sexo === 'M' ? 'Masculino' : 'Otro']}
              />
              <KeyValueRow
                left={['Identificación', `${patient.tipoDoc} ${patient.documento}`]}
                right={['Fecha de nacimiento', `${patient.fechaNacimiento} (${edad} años)`]}
              />
              <KeyValueRow
                left={['Historia clínica', patient.historiaClinica ?? '—']}
                right={['EPS', patient.eps ?? '—']}
              />
              <KeyValueRow
                left={['Teléfono', patient.telefono ?? '—']}
                right={['Ciudad', patient.ciudad ?? '—']}
              />
              <KeyValueRow
                left={['Dirección', patient.direccion ?? '—']}
                right={['Contacto de emergencia', patient.contactoEmergencia ?? '—']}
                wide
              />
              <tr>
                <td className="py-2 pr-3 align-top w-[180px] text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Alergias conocidas
                </td>
                <td colSpan={3} className="py-2 text-sm">
                  {patient.alergias !== undefined && patient.alergias.trim() !== '' ? (
                    <span className="font-semibold text-red-700">⚠ {patient.alergias}</span>
                  ) : (
                    <span className="text-slate-500">Ninguna reportada</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Episodios */}
        <section className="mb-8">
          <h2 className="text-base font-bold uppercase tracking-wider text-slate-700 border-l-4 border-slate-900 pl-2 mb-3 flex items-baseline justify-between">
            Episodios clínicos
            <span className="text-xs font-normal text-slate-500 normal-case">
              {procedures.length} episodio{procedures.length === 1 ? '' : 's'} en total
            </span>
          </h2>

          {proceduresQuery.isLoading ? (
            <div className="text-sm text-slate-500 py-3">Cargando episodios…</div>
          ) : procedures.length === 0 ? (
            <div className="text-sm text-slate-500 py-3 italic">
              No hay episodios registrados para este paciente.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {procedures.map((ep) => (
                <article
                  key={ep.id}
                  className="border border-slate-300 rounded-md p-3 break-inside-avoid"
                >
                  <header className="flex items-start justify-between gap-3 mb-2 pb-2 border-b border-slate-200">
                    <div>
                      <div className="font-bold text-slate-900">{ep.tipo}</div>
                      <div className="text-xs text-slate-600">
                        Episodio {ep.id} · {formatShortDateEs(ep.fecha)} · {ep.hora}
                        {ep.sala !== undefined && <> · {ep.sala}</>}
                      </div>
                    </div>
                    <StatePill status={ep.estado} />
                  </header>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <KeyValueInline
                      label="Médico"
                      value={userById.get(ep.medicoId) ?? ep.medicoId}
                    />
                    {ep.anestesiologoId !== undefined && (
                      <KeyValueInline
                        label="Anestesiólogo"
                        value={userById.get(ep.anestesiologoId) ?? ep.anestesiologoId}
                      />
                    )}
                    {ep.enfermeraId !== undefined && (
                      <KeyValueInline
                        label="Enfermera"
                        value={userById.get(ep.enfermeraId) ?? ep.enfermeraId}
                      />
                    )}
                    {ep.entidad !== undefined && (
                      <KeyValueInline label="Entidad" value={ep.entidad} />
                    )}
                  </div>

                  <div className="flex gap-2 mt-2 text-[11px]">
                    <DocBadge label="Consentimiento" status={ep.docs.consentimiento} />
                    <DocBadge label="Anestesia" status={ep.docs.anestesia} />
                    <DocBadge label="Hoja de gasto" status={ep.docs.gasto} />
                  </div>

                  {ep.observaciones !== undefined && ep.observaciones.trim() !== '' && (
                    <div className="mt-2 pt-2 border-t border-dashed border-slate-200 text-xs">
                      <span className="text-slate-500 font-semibold uppercase tracking-wider">
                        Observaciones:
                      </span>{' '}
                      <span className="text-slate-800">{ep.observaciones}</span>
                    </div>
                  )}

                  {ep.estado === 'anulado' && ep.motivoCancelacion !== undefined && (
                    <div className="mt-2 pt-2 border-t border-dashed border-red-200 text-xs bg-red-50 -mx-3 -mb-3 p-3 rounded-b-md">
                      <span className="text-red-700 font-semibold uppercase tracking-wider">
                        Episodio anulado:
                      </span>{' '}
                      <span className="text-red-900">{ep.motivoCancelacion}</span>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Pie */}
        <footer className="mt-12 pt-4 border-t border-slate-300 text-xs text-slate-600 leading-relaxed">
          <p className="mb-2">
            <strong>Aviso legal:</strong> este documento es una vista de referencia de la
            historia clínica del paciente. Los registros legalmente vinculantes
            (consentimiento informado, registro de anestesia y hoja de gasto) corresponden a
            los PDFs firmados digitalmente y archivados por cada episodio.
          </p>
          <p>
            Documento generado por <strong>Endomedical IPS</strong> el {generatedAt} ·{' '}
            Paciente {patient.tipoDoc} {patient.documento} · ID interno {patient.id}
          </p>
        </footer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Subcomponents                                                              */
/* -------------------------------------------------------------------------- */

interface KeyValueRowProps {
  left: [label: string, value: string];
  right: [label: string, value: string];
  wide?: boolean;
}

function KeyValueRow({ left, right, wide }: KeyValueRowProps): JSX.Element {
  return (
    <tr className={wide === true ? '' : 'border-b border-slate-100'}>
      <td className="py-2 pr-3 align-top w-[180px] text-xs uppercase tracking-wider text-slate-500 font-semibold">
        {left[0]}
      </td>
      <td className="py-2 pr-6 align-top text-sm">{left[1]}</td>
      <td className="py-2 pr-3 align-top w-[180px] text-xs uppercase tracking-wider text-slate-500 font-semibold">
        {right[0]}
      </td>
      <td className="py-2 align-top text-sm">{right[1]}</td>
    </tr>
  );
}

function KeyValueInline({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-1">
      <span className="text-slate-500 font-semibold">{label}:</span>
      <span className="text-slate-800">{value}</span>
    </div>
  );
}

function StatePill({ status }: { status: string }): JSX.Element {
  const label =
    EPISODE_STATUS_LABELS[status as keyof typeof EPISODE_STATUS_LABELS] ?? status;
  const isTerminal = status === 'cerrado';
  const isAnulado = status === 'anulado';
  return (
    <span
      className={
        'inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ' +
        (isAnulado
          ? 'bg-red-100 text-red-800 border-red-300'
          : isTerminal
            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
            : 'bg-slate-100 text-slate-700 border-slate-300')
      }
    >
      {label}
    </span>
  );
}

function DocBadge({
  label,
  status,
}: {
  label: string;
  status: 'pendiente' | 'borrador' | 'firmado';
}): JSX.Element {
  const styles =
    status === 'firmado'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
      : status === 'borrador'
        ? 'bg-amber-50 text-amber-800 border-amber-300'
        : 'bg-slate-50 text-slate-600 border-slate-300';
  const symbol = status === 'firmado' ? '✓' : status === 'borrador' ? '·' : '○';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${styles}`}>
      <span className="font-bold">{symbol}</span>
      <span>{label}</span>
    </span>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="min-h-screen grid place-items-center text-slate-500 text-sm">
      Cargando historia clínica…
    </div>
  );
}

function ErrorState({ message }: { message: string }): JSX.Element {
  return (
    <div className="min-h-screen grid place-items-center text-slate-700 px-6">
      <div className="text-center max-w-md">
        <div className="text-red-700 font-bold mb-2">No fue posible cargar la historia</div>
        <p className="text-sm text-slate-600 mb-4">{message}</p>
        <Button variant="secondary" asChild>
          <Link to="/pacientes">
            <ArrowLeft className="w-4 h-4" /> Volver al listado
          </Link>
        </Button>
      </div>
    </div>
  );
}
